import Script from 'next/script';
import { Box } from '@stitch-fix/mode-react';
import type { SocialButtonBaseProps } from './SocialButtons';
import { isRegistrationResponseExisting, registerUserWithGoogle } from '../../utils/requests/clientAuth/registration';
import { loginByGoogleToken } from '../../utils/requests/clientAuth/login';
import { isAndroidApp, loginWithGoogle } from '../../utils/registration/android';
import { SocialButton } from './SocialButton';
export type GoogleUserProfileData = {
  first_name: string;
  last_name: string;
  email: string;
};
type GoogleButtonErrorSource = 'google' | 'client_auth';
export type OnGoogleErrorParams = {
  source: GoogleButtonErrorSource;
  message?: string;
};
type GoogleButtonProps = SocialButtonBaseProps & {
  onError: (params: OnGoogleErrorParams) => void;
};
export const GoogleButton = (props: GoogleButtonProps) => {
  const handleGoogleLogin = () => {
    // If we're in an Android webview, Android will take over and
    // do the login natively. In which case, we'll just quit and
    // let it do its thing. Ideally, we'd pass a callback, which
    // it'd call once it's done, but it doesn't work that way.
    // Instead it will ultimately call `handleGoogleLogin` above
    // when it's finished.
    if (isAndroidApp()) {
      loginWithGoogle(process.env.NEXT_PUBLIC_GOOGLE_ID ?? '', 'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile');
      return;
    }
    const googleClient = window.google.accounts.oauth2.initCodeClient({
      client_id: process.env.NEXT_PUBLIC_GOOGLE_ID || '',
      scope: 'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile',
      callback: response => {
        if (response.error) {
          props.onError({
            source: 'google',
            message: response.error_description
          });
        } else if (response.code) {
          if (props.mode === 'signup') {
            const {
              businessLine,
              zipCode,
              registrationSignupType
            } = props;
            registerUserWithGoogle({
              google_oauth_token: response.code,
              business_line: businessLine,
              shipping_postcode: zipCode,
              options: {
                signup_type: registrationSignupType
              }
            }, props.isWafEnabled).then(({
              data: clientAuthRegistrationResponse
            }) => {
              if (isRegistrationResponseExisting(clientAuthRegistrationResponse)) {
                /**
                 * fire the `onLogin` callback if a user already exists
                 */
                return props.onLogin(clientAuthRegistrationResponse);
              }

              /**
               * fire the `onRegistration` callback if a new user was registered
               */
              return props.onRegistration(clientAuthRegistrationResponse);
            })
            /**
             * handle any errors from the server
             */.catch(error => props.onError({
              source: 'client_auth',
              message: error
            }));
          } else {
            loginByGoogleToken(response.code, props.isWafEnabled).then(({
              data: clientAuthRegistrationResponse
            }) => props.onLogin(clientAuthRegistrationResponse)).catch(error => props.onError({
              source: 'client_auth',
              message: error
            }));
          }
        } else {
          props.onError({
            source: 'google',
            message: 'unknown error'
          });
        }
      }
    });
    googleClient.requestCode();
  };

  // Don't load Google script when in the Android app - the app handles auth natively
  const shouldLoadScript = !isAndroidApp();
  return <Box>
      <SocialButton variant="google" onClick={handleGoogleLogin} compact={props.inline} mode={props.mode} />

      {shouldLoadScript && <div data-testid="google-script">
          <Script async defer src="https://accounts.google.com/gsi/client" />
        </div>}
    </Box>;
};